// Lessons for DSA, System Design and Agentic AI.

export const dsaLessons = [
  {
    slug: 'big-o',
    title: 'Big-O Notation',
    summary: 'Measure how code scales as input grows.',
    readTime: 5,
    content: [
      { type: 'p', text: 'Big-O describes how the running time or memory of an algorithm grows as the input size n grows. It ignores constants and focuses on the dominant term.' },
      { type: 'ul', items: [
        'O(1): constant. Accessing an array index or a hash map lookup.',
        'O(log n): logarithmic. Binary search halves the input each step.',
        'O(n): linear. One pass through an array.',
        'O(n log n): efficient sorting such as merge sort.',
        'O(n²): quadratic. Nested loops over the same input.',
        'O(2ⁿ): exponential. Trying every subset.'
      ] },
      { type: 'code', language: 'javascript', code: `// O(n²): checks every pair
function hasDuplicateSlow(arr) {
  for (let i = 0; i < arr.length; i++)
    for (let j = i + 1; j < arr.length; j++)
      if (arr[i] === arr[j]) return true;
  return false;
}

// O(n) time, O(n) space: a set remembers what we've seen
function hasDuplicate(arr) {
  const seen = new Set();
  for (const x of arr) {
    if (seen.has(x)) return true;
    seen.add(x);
  }
  return false;
}` },
      { type: 'tip', text: 'Rough guide for interviews: n up to 10⁵ usually needs O(n log n) or better. n up to 20 means exponential solutions may be acceptable.' }
    ]
  },
  {
    slug: 'two-pointers',
    title: 'Two Pointers',
    summary: 'Solve array problems in one pass.',
    readTime: 5,
    content: [
      { type: 'p', text: 'The two-pointer technique uses two indexes that move through the data, often from both ends towards the middle. It turns many O(n²) problems into O(n).' },
      { type: 'h2', text: 'Example: pair with a target sum in a sorted array' },
      { type: 'code', language: 'javascript', code: `function pairWithSum(nums, target) {
  let left = 0;
  let right = nums.length - 1;
  while (left < right) {
    const sum = nums[left] + nums[right];
    if (sum === target) return [left, right];
    if (sum < target) left++;   // need a bigger sum
    else right--;               // need a smaller sum
  }
  return null;
}

pairWithSum([1, 3, 4, 6, 9], 10); // [1, 3] -> 4 + 6` },
      { type: 'h2', text: 'When to use it' },
      { type: 'ul', items: [
        'The input is sorted, or sorting it first is acceptable.',
        'You are looking for pairs or triplets that meet a condition.',
        'Removing duplicates or partitioning in place.',
        'Checking palindromes.'
      ] },
      { type: 'tip', text: 'Explain why moving a pointer is safe. In the example, if the sum is too small, no pair using nums[left] can work, so left can move on.' }
    ]
  },
  {
    slug: 'binary-search',
    title: 'Binary Search',
    summary: 'Find answers in O(log n) by halving the search space.',
    readTime: 5,
    content: [
      { type: 'p', text: 'Binary search works on sorted data. Compare the middle element with the target and discard the half that cannot contain it.' },
      { type: 'code', language: 'javascript', code: `function binarySearch(nums, target) {
  let lo = 0;
  let hi = nums.length - 1;
  while (lo <= hi) {
    const mid = lo + Math.floor((hi - lo) / 2);
    if (nums[mid] === target) return mid;
    if (nums[mid] < target) lo = mid + 1;
    else hi = mid - 1;
  }
  return -1;
}` },
      { type: 'h2', text: 'Binary search on the answer' },
      { type: 'p', text: 'Many problems ask for the smallest value that satisfies a condition, such as the minimum speed to finish a task on time. If the condition is false up to some point and true afterwards, you can binary search over the possible answers.' },
      { type: 'code', language: 'javascript', code: `// Smallest x in [lo, hi] where isOk(x) is true
function firstTrue(lo, hi, isOk) {
  while (lo < hi) {
    const mid = lo + Math.floor((hi - lo) / 2);
    if (isOk(mid)) hi = mid;
    else lo = mid + 1;
  }
  return lo;
}` },
      { type: 'tip', text: 'Most binary search bugs are off-by-one errors. Decide whether your range is inclusive or exclusive and stay consistent.' }
    ]
  },
  {
    slug: 'bfs-and-dfs',
    title: 'Graph Traversal: BFS and DFS',
    summary: 'Explore trees, grids and graphs systematically.',
    readTime: 6,
    content: [
      { type: 'p', text: 'Breadth-first search (BFS) explores level by level using a queue. Depth-first search (DFS) goes as deep as possible first, using recursion or a stack.' },
      { type: 'code', language: 'javascript', code: `// graph = { A: ['B', 'C'], B: ['D'], C: ['D'], D: [] }

function bfs(graph, start) {
  const visited = new Set([start]);
  const queue = [start];
  const order = [];
  while (queue.length) {
    const node = queue.shift();
    order.push(node);
    for (const next of graph[node]) {
      if (!visited.has(next)) {
        visited.add(next);
        queue.push(next);
      }
    }
  }
  return order;
}

function dfs(graph, node, visited = new Set()) {
  visited.add(node);
  for (const next of graph[node]) {
    if (!visited.has(next)) dfs(graph, next, visited);
  }
  return visited;
}` },
      { type: 'h2', text: 'Which one to use' },
      { type: 'ul', items: [
        'BFS: shortest path in an unweighted graph, minimum number of steps, level-order traversal.',
        'DFS: exploring all paths, detecting cycles, topological sort, counting connected components.'
      ] },
      { type: 'tip', text: 'Grid problems such as "number of islands" are graph problems. Each cell is a node connected to its four neighbours.' }
    ]
  }
];

export const agenticAILessons = [
  {
    slug: 'llm-basics',
    title: 'How LLMs Work',
    summary: 'Tokens, context windows and calling a model.',
    readTime: 5,
    content: [
      { type: 'p', text: 'A large language model (LLM) predicts the next token, a word or part of a word, based on the text it has seen so far. Repeating this produces whole answers.' },
      { type: 'h2', text: 'Key concepts' },
      { type: 'ul', items: [
        'Tokens: models read and bill by tokens. One token is roughly three-quarters of an English word.',
        'Context window: the maximum number of tokens the model can consider at once, including your prompt and its answer.',
        'System prompt: instructions that set the model’s role and rules.',
        'Temperature: lower values give more predictable output, higher values more varied output.'
      ] },
      { type: 'h2', text: 'Calling a model' },
      { type: 'p', text: 'Most providers offer a similar messages API: you send a list of messages and receive the model’s reply.' },
      { type: 'code', language: 'javascript', code: `const response = await client.messages.create({
  model: MODEL_ID,
  max_tokens: 1024,
  system: 'You are a helpful tutor for beginner programmers.',
  messages: [{ role: 'user', content: 'Explain closures in one paragraph.' }]
});
console.log(response.content[0].text);` },
      { type: 'tip', text: 'LLMs don’t remember previous calls. To hold a conversation, send the earlier messages again with each request.' }
    ]
  },
  {
    slug: 'tool-use',
    title: 'Tool Use and the Agent Loop',
    summary: 'Let models call your functions to take action.',
    readTime: 6,
    content: [
      { type: 'p', text: 'On its own, a model can only produce text. With tool use, you describe functions it may call, such as searching the web, querying a database or sending an email. The model decides when to call them and with which arguments.' },
      { type: 'h2', text: 'Describing a tool' },
      { type: 'code', language: 'javascript', code: `const tools = [{
  name: 'get_course',
  description: 'Look up a Codeazy course by name and return its duration and modules.',
  input_schema: {
    type: 'object',
    properties: { name: { type: 'string', description: 'Course name, e.g. "System Design"' } },
    required: ['name']
  }
}];` },
      { type: 'h2', text: 'The agent loop' },
      { type: 'ol', items: [
        'Send the user’s request and the tool definitions to the model.',
        'If the model asks to use a tool, run that function in your code.',
        'Send the tool’s result back to the model.',
        'Repeat until the model returns a final answer.'
      ] },
      { type: 'tip', text: 'Good tool descriptions matter more than clever prompts. Say what the tool does, when to use it and what each parameter means.' }
    ]
  },
  {
    slug: 'rag',
    title: 'Retrieval-Augmented Generation (RAG)',
    summary: 'Ground answers in your own documents.',
    readTime: 6,
    content: [
      { type: 'p', text: 'LLMs don’t know your private data, such as your course notes, policies or code. RAG finds the relevant pieces of your data and adds them to the prompt so the model can answer from them.' },
      { type: 'h2', text: 'How it works' },
      { type: 'ol', items: [
        'Split your documents into chunks of a few hundred tokens.',
        'Convert each chunk into an embedding, a list of numbers that captures its meaning.',
        'Store the embeddings in a vector database.',
        'At question time, embed the question and fetch the most similar chunks.',
        'Send the question plus those chunks to the model and ask it to answer using only that context.'
      ] },
      { type: 'code', language: 'text', code: `Answer the question using only the context below.
If the answer is not in the context, say you don't know.

<context>
{retrieved chunks}
</context>

Question: {user question}` },
      { type: 'h2', text: 'Making it work well' },
      { type: 'ul', items: [
        'Chunk on natural boundaries such as headings and paragraphs.',
        'Combine keyword search with vector search (hybrid search) for names and codes.',
        'Ask the model to cite which chunk each fact came from.',
        'Measure retrieval quality with a test set of real questions.'
      ] },
      { type: 'tip', text: 'Most RAG failures are retrieval failures. If the right chunk isn’t in the prompt, no model can answer correctly.' }
    ]
  }
];
